/**
 * Custom Transloco MissingHandler — détecte les clés manquantes,
 * les agrège côté client (debounce 4s), puis envoie un batch au backend
 * via POST /i18n/missing. Le backend déclenchera ensuite une traduction
 * IA (ACP shim) en background, et la prochaine session bénéficiera de
 * la traduction.
 *
 * Pour ne pas casser l'UI, on retourne TOUJOURS un texte affichable :
 *   1. Si on a un fallback FR connu pour cette clé → on le retourne.
 *   2. Sinon on retourne la clé brute (le designer / dev verra l'erreur).
 *
 * Limites :
 *   - max 200 clés en buffer (le reste est ignoré jusqu'au prochain flush)
 *   - dédup par clé+locale
 *   - n'envoie rien si l'utilisateur n'est pas connecté (l'endpoint
 *     missing exige auth ; pour les clés vues sur la landing on attendra
 *     que l'utilisateur se connecte ou on retombera sur le bundle servi).
 */

import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  TranslocoMissingHandler,
  TranslocoMissingHandlerData,
} from '@jsverse/transloco';
import { CookieService } from 'ngx-cookie-service';

import { ConfigService } from '../services/config.service';

interface MissingItem {
  key: string;
  fallback?: string;
  context?: string;
}

@Injectable({ providedIn: 'root' })
export class CodincloudMissingHandler implements TranslocoMissingHandler {
  private http = inject(HttpClient);
  private cfg = inject(ConfigService);
  private cookies = inject(CookieService);

  // Buffer par locale → set de clés (évite doublons)
  private buffers = new Map<string, Map<string, MissingItem>>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly FLUSH_DELAY_MS = 4000;
  private readonly MAX_BUFFER = 200;
  private readonly FALLBACK_FR: Record<string, string> = {};

  handle(key: string, data: TranslocoMissingHandlerData, params?: any): string {
    const lang = data.activeLang || 'fr';

    // Pour le français, on accepte que la clé manque silencieusement
    // (la source de vérité reste le bundle FR, donc en théorie pas de
    //  manque ; mais en migration progressive ça peut arriver).
    if (lang !== 'fr') {
      this.recordMissing(lang, key, params);
    }

    // Si une traduction FR existe en mémoire, on l'utilise comme
    // texte temporaire pour ne pas casser l'écran.
    const frFallback = this.FALLBACK_FR[key];
    if (frFallback) return this.interpolate(frFallback, params);

    // Sinon on retourne la clé brute (visible par le dev en review)
    return key;
  }

  /** Permet de fournir un fallback FR connu (utilisé par I18nService au boot). */
  registerFrFallbacks(fr: Record<string, string>): void {
    for (const [k, v] of Object.entries(fr)) {
      if (typeof v === 'string') this.FALLBACK_FR[k] = v;
    }
  }

  private recordMissing(lang: string, key: string, params?: any): void {
    if (!key || typeof key !== 'string') return;

    let bucket = this.buffers.get(lang);
    if (!bucket) {
      bucket = new Map();
      this.buffers.set(lang, bucket);
    }
    if (bucket.size >= this.MAX_BUFFER) return;
    if (bucket.has(key)) return;

    const fallback = this.FALLBACK_FR[key];
    bucket.set(key, {
      key,
      fallback,
      context: typeof params?.context === 'string' ? params.context : undefined,
    });

    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, this.FLUSH_DELAY_MS);
  }

  private async flush(): Promise<void> {
    if (!this.cookies.get('access_token')) return;
    if (this.buffers.size === 0) return;

    const snapshots = Array.from(this.buffers.entries()).map(([lang, bucket]) => ({
      locale: lang,
      items: Array.from(bucket.values()),
    }));
    this.buffers.clear();

    const url = `${this.cfg.apiUrl}/i18n/missing`;
    const token = this.cookies.get('access_token');
    const headers = { Authorization: `Bearer ${token}` };

    for (const snap of snapshots) {
      if (!snap.items.length) continue;
      try {
        await this.http
          .post(
            url,
            { locale: snap.locale, items: snap.items },
            { headers },
          )
          .toPromise();
      } catch {
        // best-effort — on ré-essaiera la prochaine fois
      }
    }
  }

  private interpolate(text: string, params: any): string {
    if (!params || typeof params !== 'object') return text;
    return text.replace(/\{(\w+)\}/g, (_, k) =>
      params[k] !== undefined ? String(params[k]) : `{${k}}`,
    );
  }
}
